import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { getSaleDepositEditRequestPendingCount } from "../../services";
import { useUserRole } from "./useUserRole";
import { featureAccess } from "../../permissions/access";

/**
 * How often the count is refreshed while a screen is open.
 *
 * Slower than the QR duplicate badge on purpose: nothing here expires tonight — a request
 * may be raised for seven days and answered at any point inside them — so the count is worth
 * showing but not worth a request a minute from every open tab. A refresh on tab focus
 * covers the case that actually matters: someone coming back to the app to work the queue.
 */
const REFRESH_MS = 300_000;

/**
 * How many decisions this browser has taken, and who wants to know.
 *
 * The count is now read from two places at once — the tab on รายงานการโอน and the sidebar
 * entry that leads to it — and only one of them is inside the queue where decisions happen.
 * A revision passed down as a prop reached the tab and could never reach the sidebar, which
 * would then sit on the number it was mounted with until the next refresh. So the signal
 * lives beside the count it invalidates and every reader subscribes to it, rather than the
 * sidebar asking the server more often on the off-chance something changed.
 */
let decisionCount = 0;
const decisionSubscribers = new Set<() => void>();

const subscribeToDecisions = (onDecision: () => void) => {
  decisionSubscribers.add(onDecision);
  return () => {
    decisionSubscribers.delete(onDecision);
  };
};

const readDecisionCount = () => decisionCount;

/**
 * Announces that the server accepted a decision on a request: approving or rejecting one is
 * exactly what takes it out of the pending count, so every badge showing that count re-reads.
 */
export function notifySaleDepositEditRequestDecided() {
  decisionCount += 1;
  decisionSubscribers.forEach((onDecision) => onDecision());
}

/**
 * How many คำขอแก้ไขยอดโอน are waiting to be answered.
 *
 * Counted server-side under the caller's own tenant scope, and read ONLY for the grade that
 * may answer a request — the same rule that gates อนุมัติ and ปฏิเสธ. A badge says "there is
 * work waiting for you", and the branch that raised the request can do nothing about it
 * except wait; for them it was a number nagging about somebody else's inbox. They still
 * open the queue and follow their own requests, badge or no badge.
 *
 * The badge exists because this is the way the feature quietly fails. A request sits in a
 * screen nobody has a reason to open, the branch waits, and the seven days run out. Returns
 * 0 for anyone who cannot decide and never throws: a failed poll leaves the previous count
 * in place rather than blanking a queue that is still there.
 */
export function useSaleDepositEditRequestCount(): number {
  const role = useUserRole();
  const allowed = featureAccess.saleCenterClosing(role);
  const [count, setCount] = useState(0);
  const decisionsRevision = useSyncExternalStore(
    subscribeToDecisions,
    readDecisionCount,
  );

  /**
   * Which read the badge is currently waiting on.
   *
   * Two requests answered in quick succession put two counts in flight, and the slower one
   * is not necessarily the older: without this, a count taken before the second decision
   * could land last and put the answered request back on the badge.
   */
  const latestRequestRef = useRef<number>(0);

  const fetchCount = useCallback(async () => {
    if (!allowed) return;
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    try {
      const {
        data: { data },
      } = await getSaleDepositEditRequestPendingCount();
      if (latestRequestRef.current !== requestId) return;
      setCount(Number(data?.count ?? 0));
    } catch {
      // Swallowed on purpose. This is an ambient indicator on every page; a toast from it
      // would interrupt whatever the user is actually doing to report that a badge could not
      // be refreshed.
    }
  }, [allowed]);

  useEffect(() => {
    if (!allowed) {
      setCount(0);
      return;
    }
    fetchCount();
    const timer = setInterval(fetchCount, REFRESH_MS);
    const onFocus = () => fetchCount();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [allowed, fetchCount, decisionsRevision]);

  return allowed ? count : 0;
}
